import React, { useEffect, useMemo, useRef } from 'react';
import './confetti.css';

// One-shot confetti burst, fired when a brief is awarded. The
// parent mounts <ConfettiBurst /> when it wants a celebration,
// passes an `origin` (viewport coords - usually the award button's
// centre captured at click time), and unmounts the component when
// onComplete fires.
//
// Particle trajectories are computed once on mount and pushed onto
// each particle as CSS custom properties; the animation itself is
// pure CSS. Total animation duration is 2.5s; we wait ~2.8s before
// signalling complete to give the last particle time to fade.

const PARTICLE_COUNT = 56;

// Brand-only palette - Fusion Green appears twice so it dominates.
// Reds are intentionally absent (they read as warning/error here). Amber
// was dropped per the design system (brief #10 §2 / #14 Part D): amber is
// the compliance-status traffic-light colour ONLY, never decorative — a
// brand celebration uses light-green #B4E2A5 in its place.
const PARTICLE_COLOURS = [
  '#92D400', // Fusion Green
  '#92D400',
  '#B4E2A5', // Light Green (replaces off-palette amber)
  '#2E8FA1', // Teal
  '#009159', // Deep Green
  '#F9FCF1', // White
];

// Shape mix: ~50% squares, ~25% circles, ~25% chevron strips. The
// chevron is the Fusion brand chevron in miniature.
const PARTICLE_SHAPES = ['square', 'square', 'circle', 'chevron'];

// 3.5s animation + 0.3s buffer for fade-out / staggered delays.
// Exported so the parent that hosts the burst can wait for it to
// complete before transitioning away from the page that triggered
// it - otherwise the celebration gets clipped mid-flight.
export const CONFETTI_TOTAL_MS = 3800;
const ANIMATION_TOTAL_MS = CONFETTI_TOTAL_MS;

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function ConfettiBurst({ origin, onComplete }) {
  // Snapshot the reduced-motion preference once so a mid-burst
  // OS-preference change doesn't flip behaviour halfway through.
  const reducedMotion = useMemo(prefersReducedMotion, []);

  // Capture onComplete in a ref so the unmount effect doesn't
  // re-fire when the parent re-renders with a fresh callback.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; });

  const particles = useMemo(() => {
    if (reducedMotion) return [];
    return Array.from({ length: PARTICLE_COUNT }, (_, i) => {
      // Upper-arc: 10° to 170° from horizontal. Sine is positive
      // across this range so all particles initially move up; gravity
      // is added to the end-position to pull them back down.
      const angle = (10 + Math.random() * 160) * (Math.PI / 180);
      const velocity = 110 + Math.random() * 160; // 110-270, wider spread
      const txMid = Math.cos(angle) * velocity * 0.5;
      const tyMid = -Math.sin(angle) * velocity * 0.5;
      const txEnd = Math.cos(angle) * velocity;
      const tyEnd = -Math.sin(angle) * velocity + 280; // gravity
      return {
        key: i,
        shape: PARTICLE_SHAPES[Math.floor(Math.random() * PARTICLE_SHAPES.length)],
        colour: PARTICLE_COLOURS[Math.floor(Math.random() * PARTICLE_COLOURS.length)],
        txMid, tyMid, txEnd, tyEnd,
        delay: Math.random() * 0.15, // gentle stagger so particles don't lock-step
      };
    });
  }, [reducedMotion]);

  useEffect(() => {
    if (reducedMotion) {
      // Honour the preference: no particles, no waiting around.
      onCompleteRef.current?.();
      return undefined;
    }
    const timer = setTimeout(() => onCompleteRef.current?.(), ANIMATION_TOTAL_MS);
    return () => clearTimeout(timer);
  }, [reducedMotion]);

  if (reducedMotion) return null;

  // Fallback to viewport centre if the caller didn't pin an origin
  // (e.g. the source element unmounted before we snapshotted its
  // position). Better than nothing.
  const safeOrigin = origin ?? {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  };

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: safeOrigin.x,
        top: safeOrigin.y,
        width: 0,
        height: 0,
        pointerEvents: 'none',
        zIndex: 50,
      }}>
      {particles.map(p => (
        <div
          key={p.key}
          className={`confetti-particle confetti-particle--${p.shape}`}
          style={{
            backgroundColor: p.colour,
            animationDelay: `${p.delay}s`,
            '--tx-mid': `${p.txMid}px`,
            '--ty-mid': `${p.tyMid}px`,
            '--tx-end': `${p.txEnd}px`,
            '--ty-end': `${p.tyEnd}px`,
          }} />
      ))}
    </div>
  );
}
